import React, { useState, useEffect } from 'react';
import SiteNav from './components/SiteNav';
import BookCard from './components/BookCard';
import { API_URL } from './utils/books';
import './App.css';

function HomePage() {
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [showGenreFilter, setShowGenreFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage] = useState(12);

  useEffect(() => {
    fetch(`${API_URL}/api/health`)
      .then((response) => response.json())
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    fetch(`${API_URL}/api/books`)
      .then((response) => response.json())
      .then((data) => {
        if (data.books) {
          setBooks(data.books);
          setFilteredBooks(data.books);
        }
      })
      .catch((err) => console.error('Error fetching books:', err));
  }, []);

  useEffect(() => {
    let filtered = books.filter((book) => {
      const bookName = book.name || '';
      const bookAuthor = book.author || '';
      const bookGenre = book.genre || '';

      const matchesSearch =
        bookName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookAuthor.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGenre =
        selectedGenre === '' || bookGenre.toLowerCase().includes(selectedGenre.toLowerCase());
      return matchesSearch && matchesGenre;
    });
    filtered = [...filtered].sort(
      (a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured))
    );
    setFilteredBooks(filtered);
    setCurrentPage(1);
  }, [books, searchTerm, selectedGenre]);

  const toggleCardFlip = (bookId) => {
    setFlippedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(bookId)) {
        newSet.delete(bookId);
      } else {
        newSet.add(bookId);
      }
      return newSet;
    });
  };

  const getUniqueGenres = () => {
    const allGenres = books.flatMap((book) => (book.genre || '').split(', '));
    return [...new Set(allGenres)].filter((genre) => genre.trim() !== '').sort();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGenre('');
  };

  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  if (loading) {
    return (
      <div className="App">
        <SiteNav />
        <header className="App-header">
          <h1>Loading...</h1>
        </header>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <SiteNav />
        <header className="App-header">
          <h1>Error</h1>
          <p>{error}</p>
        </header>
      </div>
    );
  }

  return (
    <div
      className="App"
      style={{
        backgroundImage: `url('/background/background.png')`,
        backgroundColor: '#f0f0f0'
      }}
    >
      <SiteNav />

      <header className="App-header">
        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by book name or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowGenreFilter(!showGenreFilter)}
                className="filter-button"
              >
                <span className="filter-icon">⚙️</span>
              </button>
              {showGenreFilter && (
                <div className="genre-filter">
                  <select
                    value={selectedGenre}
                    onChange={(e) => setSelectedGenre(e.target.value)}
                    className="genre-select"
                  >
                    <option value="">All Genres</option>
                    {getUniqueGenres().map((genre) => (
                      <option key={genre} value={genre}>
                        {genre}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {(searchTerm || selectedGenre) && (
              <button onClick={clearFilters} className="clear-button">
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="books-section">
          {filteredBooks.length > 0 && (
            <div className="results-info">
              Showing {indexOfFirstBook + 1}-
              {Math.min(indexOfLastBook, filteredBooks.length)} of {filteredBooks.length} books
            </div>
          )}
          <div className="books-grid">
            {currentBooks.length > 0 ? (
              currentBooks.map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  isFlipped={flippedCards.has(book.id)}
                  onToggle={() => toggleCardFlip(book.id)}
                />
              ))
            ) : (
              <p>No books found or database not connected</p>
            )}
          </div>

          {filteredBooks.length > booksPerPage && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>

              <div className="pagination-numbers">
                {currentPage > 3 && totalPages > 5 && (
                  <>
                    <button
                      className="pagination-number"
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                    <span className="pagination-ellipsis">...</span>
                  </>
                )}

                {getPageNumbers().map((number) => (
                  <button
                    key={number}
                    className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                    onClick={() => handlePageChange(number)}
                  >
                    {number}
                  </button>
                ))}

                {currentPage < totalPages - 2 && totalPages > 5 && (
                  <>
                    <span className="pagination-ellipsis">...</span>
                    <button
                      className="pagination-number"
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                className="pagination-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </header>
    </div>
  );
}

export default HomePage;
